/**
 * Facebook Pixel `Purchase`, sent once per actual charge: the trial/subscription
 * on the personal-plan page, and each successful upsell.
 *
 * Gated by the `sendFbPurchaseEvent` remote-config flag — the caller passes it in,
 * since the two call sites read remote config differently. With the flag off,
 * nothing is sent.
 *
 * `value` is what was really charged, not the plan price: for a trial that is the
 * trial amount, which is usually far smaller. Sending the plan price would inflate
 * reported revenue and skew the ad optimisation this event feeds.
 */
type Params = {
  enabled?: boolean;
  /** Amount actually charged. Strings with currency symbols are accepted. */
  value?: number | string;
  currency?: string;
  /** Distinguishes the two events in the Pixel debugger. */
  contentName?: string;
};

const toNumber = (value?: number | string) => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }

  if (typeof value !== 'string') {
    return null;
  }

  // Configs hold prices as "$19.99" / "19,99" — keep digits and the separator.
  const parsed = parseFloat(value.replace(',', '.').replace(/[^\d.]/g, ''));
  return Number.isFinite(parsed) ? parsed : null;
};

export const trackFbPurchase = ({ enabled, value, currency, contentName }: Params): void => {
  if (!enabled) {
    return;
  }

  const amount = toNumber(value);

  // A Purchase with no value is worse than no event: it reports a conversion worth
  // nothing and drags the campaign's average down.
  if (amount === null) {
    console.warn('[FB] Purchase not sent — no usable value', { value, contentName });
    return;
  }

  const fbq = (window as any).fbq;

  if (typeof fbq !== 'function') {
    return;
  }

  try {
    fbq('track', 'Purchase', {
      value: amount,
      currency: (currency || 'USD').toUpperCase(),
      ...(contentName ? { content_name: contentName } : {}),
    });
  } catch (e) {
    // The pixel walks cross-origin iframes internally and can throw on them
    // (Collect.js mounts its card fields in one). Never let that break the
    // post-purchase flow.
    console.warn('[FB] Purchase event failed', e);
  }
};

export default trackFbPurchase;
